import axios from 'axios';
import { Config } from '../config';

export const api = axios.create({
  baseURL: Config.APIBaseURL,
});

// Add access_token header to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.access_token = `Bearer ${token}`;
  }
  return config;
});

// ========================================
// Authentication API
// ========================================
export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials),
  signup: (newuser, userinfo, invitation_token = null) => {
    const params = {};
    if (invitation_token) {
      params.invitation_token = invitation_token;
    }
    return api.post('/auth/signup', { newuser, userinfo }, { params });
  },
  logout: () => api.get('/auth/logout'),
  verify2fa: (tempToken, code) => api.post('/auth/verify-2fa', { temp_token: tempToken, code }),
  get2faStatus: () => api.get('/user/2fa/status'),
  setup2fa: () => api.get('/user/2fa/setup'),
  enable2fa: (code) => api.post('/user/2fa/enable', { code }),
  disable2fa: (password) => api.post('/user/2fa/disable', { password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  getCurrentUser: () => api.get('/user/me'),
  getAssessmentInfo: () => api.get('/users/me/assessment-info'),
  updateProfile: (data) => api.put('/user/profile', data),
  uploadMyAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/user/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateLanguage: (language) => api.put('/user/language', { language }),
};

// ========================================
// Invitation API
// ========================================
export const invitationApi = {
  // Public
  validateToken: (token) => api.get(`/invites/validate/${token}`),
  
  // Admin only
  create: (assessmentId, email, oesId, allowedEmailDomain, expiresInDays = 7) => 
    api.post('/admin/invites', null, { 
      params: { 
        assessment_id: assessmentId,
        email: email,
        oes_id: oesId,
        allowed_email_domain: allowedEmailDomain || undefined,
        expires_in_days: expiresInDays 
      } 
    }),
  getAll: (skip = 0, limit = 100, includeUsed = false, assessmentId = null) => {
    let url = `/admin/invites?skip=${skip}&limit=${limit}&include_used=${includeUsed}`;
    if (assessmentId) url += `&assessment_id=${assessmentId}`;
    return api.get(url);
  },
  getByOES: (oesId, includeUsed = false) => 
    api.get(`/admin/invites/oes/${oesId}?include_used=${includeUsed}`),
  delete: (inviteId) => api.delete(`/admin/invites/${inviteId}`),
};

// ========================================
// Organization API
// ========================================
export const organizationApi = {
  getAll: (skip = 0, limit = 100) => api.get(`/organizations?skip=${skip}&limit=${limit}`),
  getById: (id) => api.get(`/organizations/${id}`),
  create: (orgData) => api.post('/organizations', orgData),
  update: (id, orgData) => api.put(`/organizations/${id}`, orgData),
  delete: (id) => api.delete(`/organizations/${id}`),
  getAdmins: (orgId) => api.get(`/organizations/${orgId}/admins`),
  assignAdmin: (orgId, userId) => api.post(`/organizations/${orgId}/admins/${userId}`),
  removeAdmin: (orgId, userId) => api.delete(`/organizations/${orgId}/admins/${userId}`),
};

// ========================================
// OES (Operator of Essential Services) API
// ========================================
export const oesApi = {
  create: (oesData) => api.post('/oes', oesData),
  getAll: (skip = 0, limit = 100, sector = null, criticality = null, assessmentId = null) => {
    let url = `/oes?skip=${skip}&limit=${limit}`;
    if (sector) url += `&sector=${sector}`;
    if (criticality) url += `&criticality=${criticality}`;
    if (assessmentId) url += `&assessment_id=${assessmentId}`;
    return api.get(url);
  },
  getById: (id) => api.get(`/oes/${id}`),
  update: (id, oesData) => api.put(`/oes/${id}`, oesData),
  delete: (id) => api.delete(`/oes/${id}`),
  removeFromAssessment: (id) => api.post(`/oes/${id}/remove-from-assessment`),
  getAssets: (oesId, skip = 0, limit = 100) => 
    api.get(`/oes/${oesId}/assets?skip=${skip}&limit=${limit}`),
  
  // Criticality Assessment
  createAssessment: (oesId, assessmentData) => 
    api.post(`/oes/${oesId}/criticality-assessment`, assessmentData),
  getCurrentAssessment: (oesId) => 
    api.get(`/oes/${oesId}/criticality-assessment`),
  getAssessmentById: (assessmentId) => 
    api.get(`/oes-assessments/${assessmentId}`),
  getAssessmentHistory: (oesId, currentOnly = false) => 
    api.get(`/oes/${oesId}/criticality-assessments?current_only=${currentOnly}`),
  updateAssessment: (assessmentId, assessmentData) => 
    api.put(`/oes-assessments/${assessmentId}`, assessmentData),
  updateProgress: (oesId, completedPhases) => 
    api.patch(`/oes/${oesId}/criticality-assessment/progress`, completedPhases),
  /** Assess with AI: get LLM suggestions for one phase (services | processes | cyberattack) */
  aiSuggest: (oesId, phase) =>
    api.post(`/oes/${oesId}/criticality-assessment/ai-suggest`, { phase }),

  // Posture questionnaire (per OES)
  getQuestionnaire: (oesId) => api.get(`/oes/${oesId}/questionnaire`),
  submitQuestionnaire: (oesId, payload) => api.post(`/oes/${oesId}/questionnaire`, payload),
  getQuestionnaireResult: (oesId) => api.get(`/oes/${oesId}/questionnaire/result`),
  getPotentialVulnerabilities: (oesId, assessmentId = null, latestOnly = true) => {
    const params = { latest_only: latestOnly };
    if (assessmentId != null) params.assessment_id = assessmentId;
    return api.get(`/oes/${oesId}/potential-vulnerabilities`, { params });
  },
  getThreatRisks: (oesId, assessmentId = null, latestOnly = true) => {
    const params = { latest_only: latestOnly };
    if (assessmentId != null) params.assessment_id = assessmentId;
    return api.get(`/oes/${oesId}/threat-risks`, { params });
  },
  getAssetRiskMatrix: (oesId, assessmentId = null) =>
    api.get(`/oes/${oesId}/asset-risk-matrix`, { params: assessmentId != null ? { assessment_id: assessmentId } : {} }),
  patchPotentialVulnerabilityDescription: (oesId, pvId, body) =>
    api.patch(`/oes/${oesId}/potential-vulnerabilities/${pvId}`, body),
  deletePotentialVulnerability: (oesId, pvId) =>
    api.delete(`/oes/${oesId}/potential-vulnerabilities/${pvId}`),
  patchRiskMatrixCell: (oesId, body) =>
    api.patch(`/oes/${oesId}/asset-risk-matrix/cell`, body),
  patchRiskMatrixVulnerabilityCell: (oesId, body) =>
    api.patch(`/oes/${oesId}/asset-risk-matrix/vulnerability-cell`, body),
  deleteQuestionnaireResponse: (oesId, assessmentId) => api.delete(`/oes/${oesId}/questionnaire`, { params: { assessment_id: assessmentId } }),

  // User Management
  getUsers: (oesId) => api.get(`/oes/${oesId}/users`),
  assignUser: (oesId, userId) => api.post(`/oes/${oesId}/users/${userId}`),
  removeUser: (oesId, userId) => api.delete(`/oes/${oesId}/users/${userId}`),
};

// ========================================
// Criticality Matrix API
// ========================================
export const criticalityMatrixApi = {
  getMatrix: () => api.get('/criticality-matrix'),
};

// ========================================
// Critical Assets (IT/OT Systems) API
// ========================================
export const assetApi = {
  create: (assetData) => api.post('/assets', assetData),
  getAll: (skip = 0, limit = 100, oesId = null, assetValue = null) => {
    let url = `/assets?skip=${skip}&limit=${limit}`;
    if (oesId) url += `&oes_id=${oesId}`;
    if (assetValue) url += `&asset_value=${assetValue}`;
    return api.get(url);
  },
  getById: (id) => api.get(`/assets/${id}`),
  update: (id, assetData) => api.put(`/assets/${id}`, assetData),
  delete: (id) => api.delete(`/assets/${id}`),
  getByOwner: (ownerId, skip = 0, limit = 100) => 
    api.get(`/assets?owner_id=${ownerId}&skip=${skip}&limit=${limit}`),
};

// ========================================
// Threat Actors API
// ========================================
export const threatActorApi = {
  create: (actorData) => api.post('/threat-actors', actorData),
  getAll: (skip = 0, limit = 100) => api.get(`/threat-actors?skip=${skip}&limit=${limit}`),
  getById: (id) => api.get(`/threat-actors/${id}`),
  update: (id, actorData) => api.put(`/threat-actors/${id}`, actorData),
};

// ========================================
// Vulnerabilities API
// ========================================
export const vulnerabilityApi = {
  create: (vulnData) => api.post('/vulnerabilities', vulnData),
  getByAsset: (assetId) => api.get(`/assets/${assetId}/vulnerabilities`),
  getOpen: (skip = 0, limit = 100) => api.get(`/vulnerabilities/open?skip=${skip}&limit=${limit}`),
  update: (id, vulnData) => api.put(`/vulnerabilities/${id}`, vulnData),
};

// ========================================
// Risk Assessments API
// ========================================
export const riskAssessmentApi = {
  create: (assessmentData) => api.post('/risk-assessments', assessmentData),
  getByAsset: (assetId, currentOnly = false) => 
    api.get(`/assets/${assetId}/risk-assessments?current_only=${currentOnly}`),
  update: (id, assessmentData) => api.put(`/risk-assessments/${id}`, assessmentData),
  getHighRisk: (limit = 10) => api.get(`/risk-assessments/high-risk?limit=${limit}`),
};

// ========================================
// Security Controls API
// ========================================
export const securityControlApi = {
  create: (controlData) => api.post('/security-controls', controlData),
  getByAsset: (assetId) => api.get(`/assets/${assetId}/controls`),
  update: (id, controlData) => api.put(`/security-controls/${id}`, controlData),
};

// ========================================
// Security Incidents API
// ========================================
export const securityIncidentApi = {
  create: (incidentData) => api.post('/security-incidents', incidentData),
  getByAsset: (assetId) => api.get(`/assets/${assetId}/incidents`),
  getRecent: (limit = 10) => api.get(`/security-incidents/recent?limit=${limit}`),
  update: (id, incidentData) => api.put(`/security-incidents/${id}`, incidentData),
};

// ========================================
// Dashboard & Analytics API
// ========================================
export const dashboardApi = {
  getSummary: () => api.get('/dashboard/summary'),
  getAssetStats: () => api.get('/analytics/asset-statistics'),
  getRiskStats: () => api.get('/analytics/risk-statistics'),
  getVulnerabilityStats: () => api.get('/analytics/vulnerability-statistics'),
};

// ========================================
// Admin API - User Management
// ========================================
export const adminApi = {
  // User Management
  getAllUsers: (skip = 0, limit = 100) => api.get(`/users?skip=${skip}&limit=${limit}`),
  getUserById: (userId) => api.get(`/users/${userId}`),
  createUser: (newuser, userinfo) => api.post('/users', { newuser, userinfo }),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
  updateUserInfo: (userId, userInfoData) => api.put(`/users/${userId}/info`, userInfoData),
  uploadAvatar: (userId, file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post(`/users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // OES Management (admin functions)
  createOES: (oesData) => api.post('/oes', oesData),
  bulkCreateOES: (payload) => api.post('/oes/bulk', payload),
  getAllOES: (skip = 0, limit = 100) => api.get(`/oes?skip=${skip}&limit=${limit}`),
  getOESById: (oesId) => api.get(`/oes/${oesId}`),
  updateOES: (oesId, oesData) => api.put(`/oes/${oesId}`, oesData),
  deleteOES: (oesId) => api.delete(`/oes/${oesId}`),
  uploadOESLogo: (oesId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post(`/oes/${oesId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Sector Lists Management
  getSectorLists: (organizationId = null) => {
    const params = organizationId ? { organization_id: organizationId } : {};
    return api.get('/sector-lists', { params });
  },
  getSectorListById: (listId) => api.get(`/sector-lists/${listId}`),
  createSectorList: (listData) => api.post('/sector-lists', listData),
  updateSectorList: (listId, listData) => api.put(`/sector-lists/${listId}`, listData),
  deleteSectorList: (listId) => api.delete(`/sector-lists/${listId}`),

  // Sectors Management
  getSectorsByList: (listId) => api.get(`/sector-lists/${listId}/sectors`),
  addSector: (listId, sectorData) => api.post(`/sector-lists/${listId}/sectors`, sectorData),
  updateSector: (sectorId, sectorData) => api.put(`/sectors/${sectorId}`, sectorData),
  deleteSector: (sectorId) => api.delete(`/sectors/${sectorId}`),
};

// ========================================
// Admin Criticality Assessments API (admin only)
// ========================================
export const adminCriticalityApi = {
  getCompletedAssessments: (currentOnly = true) => api.get('/admin/completed-criticality-assessments', { params: { current_only: currentOnly } }),
  getCompletedPostureAssessments: () => api.get('/admin/completed-posture-assessments'),
  getDashboardMetrics: () => api.get('/admin/criticality-dashboard-metrics'),
  getPostureDashboardMetrics: () => api.get('/admin/posture-dashboard-metrics'),
  getRiskDashboardMetrics: () => api.get('/admin/risk-dashboard-metrics'),
  deleteCriticalityResults: (assessmentId) => api.delete(`/oes-assessments/${assessmentId}`),
};

// ========================================
// Settings API (admin: global and per-organization)
// ========================================
export const settingsApi = {
  getEditableScopes: () => api.get('/settings/editable-scopes'),
  /** Resolved settings for current user (OES org for users, global for admins). Use for assessment pages. */
  getForCurrentUser: () => api.get('/user/settings'),
  get: (scope = 'global', organizationId = null) => {
    const params = { scope };
    if (scope === 'organization' && organizationId != null) params.organization_id = organizationId;
    return api.get('/settings', { params });
  },
  put: (payload) => api.put('/settings', payload),
  /** System settings (SMTP etc). Global admin only. */
  getSystem: () => api.get('/settings/system'),
  putSystem: (payload) => api.put('/settings/system', payload),
  /** Security / password policy. Global admin only. */
  getSecurity: () => api.get('/settings/security'),
  putSecurity: (payload) => api.put('/settings/security', payload),
  /** AI configuration. Global admin only. */
  getAI: () => api.get('/settings/ai'),
  putAI: (payload) => api.put('/settings/ai', payload),
  getOllamaModels: () => api.get('/settings/ai/ollama-models'),
  getSectorDeps: () => api.get('/settings/sector-deps'),
  putSectorDeps: (payload) => api.put('/settings/sector-deps', payload),
  /** Account lockout policy. Global admin only. */
  getSecurityLockout: () => api.get('/settings/security/lockout'),
  putSecurityLockout: (payload) => api.put('/settings/security/lockout', payload),
  /** Require 2FA policy. Global admin only. Values: off | admins | all */
  getSecurityRequire2fa: () => api.get('/settings/security/require-2fa'),
  putSecurityRequire2fa: (require_2fa) => api.put('/settings/security/require-2fa', { require_2fa }),
};

// ========================================
// Admin audit logs (event viewer). Global admin only.
// ========================================
export const auditLogsApi = {
  getList: ({ skip = 0, limit = 50, activity_type, user_id, date_from, date_to } = {}) => {
    const params = { skip, limit };
    if (activity_type) params.activity_type = activity_type;
    if (user_id != null) params.user_id = user_id;
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    return api.get('/admin/audit-logs', { params });
  },
};

// ========================================
// Posture request API
// ========================================
export const postureRequestApi = {
  create: (oesId, assessmentId) => api.post('/admin/posture-request', { oes_id: oesId, assessment_id: assessmentId }),
  list: (assessmentId = null) => {
    const params = assessmentId != null ? { assessment_id: assessmentId } : {};
    return api.get('/admin/posture-requests', { params });
  },
  delete: (oesId, assessmentId) => api.delete('/admin/posture-request', { params: { oes_id: oesId, assessment_id: assessmentId } }),
  getStatus: (oesId, assessmentId) => api.get('/user/posture-request', { params: { oes_id: oesId, assessment_id: assessmentId } }),
};

// ========================================
// Reports API (admin only)
// ========================================
export const reportsApi = {
  getSummary: (assessmentId = null) => {
    const params = assessmentId ? { assessment_id: assessmentId } : {};
    return api.get('/admin/reports/summary', { params });
  },
  exportCriticality: (assessmentId = null) => {
    const params = assessmentId ? { assessment_id: assessmentId } : {};
    return api.get('/admin/reports/export/criticality', { params, responseType: 'blob' });
  },
  exportPosture: (assessmentId = null) => {
    const params = assessmentId ? { assessment_id: assessmentId } : {};
    return api.get('/admin/reports/export/posture', { params, responseType: 'blob' });
  },
  getFullReport: (assessmentId = null) => {
    const params = assessmentId ? { assessment_id: assessmentId } : {};
    return api.get('/admin/reports/full', { params });
  },
  exportRisk: (assessmentId = null) => {
    const params = assessmentId ? { assessment_id: assessmentId } : {};
    return api.get('/admin/reports/export/risk', { params, responseType: 'blob' });
  },
};

// ========================================
// Assessment Management API
// ========================================
export const assessmentApi = {
  // Get all assessments (role-filtered)
  getAll: (skip = 0, limit = 100, onlyActive = false) => 
    api.get(`/assessments?skip=${skip}&limit=${limit}&only_active=${onlyActive}`),
  
  // Get specific assessment
  getById: (id) => api.get(`/assessments/${id}`),
  
  // Create new assessment (global admin only)
  create: (assessmentData) => api.post('/assessments', assessmentData),
  
  // Update assessment
  update: (id, assessmentData) => api.put(`/assessments/${id}`, assessmentData),
  
  // Update assessment status (start, pause, end)
  updateStatus: (id, status) => api.patch(`/assessments/${id}/status`, null, { params: { status } }),
  
  // Delete (deactivate) assessment (global admin only)
  delete: (id) => api.delete(`/assessments/${id}`),
  
  // Get assessment admins
  getAdmins: (id) => api.get(`/assessments/${id}/admins`),
  
  // Assign assessment admin (global admin only)
  assignAdmin: (assessmentId, userId) => 
    api.post(`/assessments/${assessmentId}/admins/${userId}`),
  
  // Remove assessment admin (global admin only)
  removeAdmin: (assessmentId, userId) => 
    api.delete(`/assessments/${assessmentId}/admins/${userId}`),
};

// ========================================
// Posture / gap analysis: frameworks & controls
// ========================================
export const frameworksApi = {
  getAll: (skip = 0, limit = 100) => api.get(`/frameworks?skip=${skip}&limit=${limit}`),
  getControls: (frameworkId) => api.get(`/frameworks/${frameworkId}/controls`),
};

// ========================================
// Gap results (posture assessment) per assessment
// ========================================
export const gapResultsApi = {
  getByAssessment: (assessmentId, oesId = null) => {
    const params = oesId ? { oes_id: oesId } : {};
    return api.get(`/assessments/${assessmentId}/gap_results`, { params });
  },
  save: (assessmentId, payload, oesId = null) => {
    const params = oesId ? { oes_id: oesId } : {};
    return api.post(`/assessments/${assessmentId}/gap_results`, payload, { params });
  },
};

// ========================================
// Instance API
// ========================================
export const instanceApi = {
  getMode: () => api.get('/instance/mode'),
  getLabels: () => api.get('/config/labels'),
};

// ========================================
// Integrations API
// ========================================
// ========================================
// Agent API (OES side — key management)
// ========================================
export const agentApi = {
  getKeys: () => api.get('/agent/keys'),
  createKey: (data) => api.post('/agent/keys', data),
  updateKey: (id, data) => api.put(`/agent/keys/${id}`, data),
  deleteKey: (id) => api.delete(`/agent/keys/${id}`),
};

// ========================================
// Agent Manager API (Coordinator side)
// ========================================
export const agentManagerApi = {
  getAll: () => api.get('/admin/agents'),
  create: (data) => api.post('/admin/agents', data),
  update: (id, data) => api.put(`/admin/agents/${id}`, data),
  delete: (id) => api.delete(`/admin/agents/${id}`),
  test: (id) => api.post(`/admin/agents/${id}/test`),
  pull: (id) => api.post(`/admin/agents/${id}/pull`),
  getSnapshots: (id, params = {}) => api.get(`/admin/agents/${id}/snapshots`, { params }),
  getLatest: (id) => api.get(`/admin/agents/${id}/latest`),
};

export const integrationsApi = {
  getAll: () => api.get('/integrations'),
  getById: (id) => api.get(`/integrations/${id}`),
  create: (data) => api.post('/integrations', data),
  update: (id, data) => api.put(`/integrations/${id}`, data),
  delete: (id) => api.delete(`/integrations/${id}`),
  testConnection: (id) => api.post(`/integrations/${id}/test`),
  addOESMapping: (id, data) => api.post(`/integrations/${id}/oes-mappings`, data),
  removeOESMapping: (id, oesId) => api.delete(`/integrations/${id}/oes-mappings/${oesId}`),
  syncOES: (integrationId, oesId) => api.post(`/integrations/${integrationId}/sync/${oesId}`),
  getSyncs: (params = {}) => api.get('/integrations/syncs', { params }),
  getOESSummary: (oesId) => api.get(`/oes/${oesId}/integration-summary`),
  getOESFindings: (oesId, params = {}) => api.get(`/oes/${oesId}/integration-findings`, { params }),
};
